import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, ValidationError } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import type { Args as FormSelectArgs, OptionRaw } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import RowErrorComponent from 'webapp/components/row-error';

import type { MetricSetup, MetricSetupForClient } from 'webapp/models-custom';
import MetricSetupForMasterFilter from 'webapp/models-custom/metric-setup-for-master-filter';

import type { HorizonBreakdownOffer } from 'webapp/services/model-manager/horizon-metrics';

export type Options = OptionRaw<string>[];

// sentinel for the single-mode "None" row; never a real dimension key
// exported for legacy consumers (custom-query dimension-selector); new code should not rely on it
export const NONE = '_?n0n3-!.';

// the most splits a block takes (the server refuses more)
const MAX_DIMENSIONS = 10;

export type MetricSetupType = MetricSetup | MetricSetupForClient | MetricSetupForMasterFilter;

export interface Args {
	metricSetup: MetricSetupType;
	onChange: (value: string[]) => void;
	isMultiple?: boolean; // when true, render a multi-select (no "None" row)
	skipNone?: boolean; // single mode only: hide the "None" row
	allowEmpty?: boolean; // when true, empty selection is a valid commit (no error)
	context?: string;
	placeholder?: FormSelectArgs['placeholder'];
	isDisabled?: FormSelectArgs['isDisabled'];
	showFloatlabel?: FormSelectArgs['showFloatlabel'];
	theme?: FormSelectArgs['theme'];
	size?: FormSelectArgs['size'];
}

export default class FormSelectDimensionsComponent<T extends Args> extends Component<T> {
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;

	uniqueId = 'select-' + guidFor(this);

	// Horizon only: which splits still fit the block's selection, and which picked ones it refuses together,
	// with the request (metric and selection) the answer belongs to
	@tracked private loadedHorizonOffer: {
		key: string;
		offer: HorizonBreakdownOffer | null;
	} | null = null;

	private requestedHorizonOfferKey = '';

	constructor(owner: Owner, args: T) {
		super(owner, args);
		void this.loadHorizonOffer();
	}

	get horizonMetricObjectId(): number | null {
		const metricSetup = this.args.metricSetup;
		// resolve needs one metric: a master filter spans several
		if (!this.args.isMultiple || metricSetup instanceof MetricSetupForMasterFilter) {
			return null;
		}
		const metric = (metricSetup as MetricSetup).metric;
		return metric?.isTypeHorizon && metric.objectId ? metric.objectId : null;
	}

	// changes with the metric and its selection, also when they change outside this picker (a metric switch)
	get horizonOfferRequestKey(): string {
		const objectId = this.horizonMetricObjectId;
		return objectId ? `${objectId}|${this.args.metricSetup.dimensions.join(',')}` : '';
	}

	// Horizon's answer for exactly this metric and selection
	get horizonOffer(): HorizonBreakdownOffer | null {
		const loaded = this.loadedHorizonOffer;
		const key = this.horizonOfferRequestKey;
		return key && loaded?.key === key ? loaded.offer : null;
	}

	// The splits the picker narrows to. Nothing picked narrows nothing, as before several splits. While the answer
	// for a new selection loads, the last one for this metric is kept, so the full list doesn't flash back.
	get horizonAddable(): Set<string> | null {
		const objectId = this.horizonMetricObjectId;
		const loaded = this.loadedHorizonOffer;
		if (!objectId || !loaded || !this.args.metricSetup.dimensions.length) {
			return null;
		}
		return loaded.key.startsWith(`${objectId}|`) ? (loaded.offer?.addable ?? null) : null;
	}

	// only with two or more splits: the server checks a single split as before
	get refusedDimensionErrorText() {
		const refused = this.horizonOffer?.refused;
		if (!refused?.size || this.args.metricSetup.dimensions.length < 2) {
			return '';
		}
		const labels = this.args.metricSetup.metricDimensions
			.filter((d) => refused.has(d.key))
			.map((d) => d.value);
		return `This metric cannot be split by ${labels.join(', ')} together with the other selected dimensions.`;
	}

	get isUnspecifiedMasterSelected() {
		// for master filters - when we have mixed dimensions selected
		const metricSetup = this.args.metricSetup;
		return (
			metricSetup instanceof MetricSetupForMasterFilter &&
			metricSetup.isUnspecificDimensionSelected
		);
	}

	get floatlabelText() {
		return 'Breakdown by';
	}

	get placeholder() {
		return this.args.placeholder || `Select dimension${this.args.isMultiple ? 's' : ''}`;
	}

	get allowNone() {
		return !this.args.skipNone && !this.args.metricSetup.mustSelectDimension;
	}

	get selectedSingle() {
		if (this.isUnspecifiedMasterSelected) {
			return '';
		}
		return this.args.metricSetup.dimensions[0] || (this.args.allowEmpty ? '' : NONE);
	}

	get selectedMultiple(): string[] {
		if (this.isUnspecifiedMasterSelected) {
			return [];
		}
		return this.args.metricSetup.dimensions;
	}

	get invalidDimensionErrorText() {
		if (this.isSelectedDimensionInvalid) {
			return 'Selected dimension is not recognized.';
		}
		return '';
	}

	get errors() {
		const errors: ValidationError[] = [];
		const dimensions = this.args.metricSetup.dimensions;
		const mustSelect = this.args.metricSetup.mustSelectDimension;

		if (dimensions.length === 0 && !this.args.allowEmpty && mustSelect) {
			errors.push({ message: 'Must select a dimension.' });
		} else if (this.isSelectedDimensionInvalid) {
			errors.push({ message: this.invalidDimensionErrorText });
		} else if (this.refusedDimensionErrorText) {
			errors.push({ message: this.refusedDimensionErrorText });
		}
		return errors;
	}

	@cached
	get isSelectedDimensionInvalid() {
		if (this.isUnspecifiedMasterSelected) {
			return false;
		}
		const validKeys = new Set(this.args.metricSetup.metricDimensions.map((d) => d.key));
		return this.args.metricSetup.dimensions.some((d) => !validKeys.has(d));
	}

	@cached
	get options(): Options {
		const options: Options = [];

		if (!this.args.isMultiple && this.allowNone) {
			options.push({
				value: NONE,
				label: 'None',
			});
		}

		const addable = this.horizonAddable;
		const selected = new Set(this.args.metricSetup.dimensions);
		const isFull = this.args.isMultiple && selected.size >= MAX_DIMENSIONS;
		this.args.metricSetup.metricDimensions.forEach((d) => {
			// Horizon: a split that no longer fits stays in the list, disabled, so the open list keeps its
			// length and position after a pick (a refused picked one is named below)
			const fits = !addable || selected.has(d.key) || addable.has(d.key);
			let disabledReason: string | null = null;
			if (isFull && !selected.has(d.key)) {
				disabledReason = `You can select up to ${MAX_DIMENSIONS} dimensions`;
			} else if (!fits) {
				disabledReason = "Can't be combined with the selected dimensions";
			}
			options.push({
				value: d.key,
				label: d.value,
				...(disabledReason ? { disabled: true, description: disabledReason } : {}),
			});
		});

		return options;
	}

	@action
	onChangeSingle(dimension: string | null) {
		const dimensions = dimension && dimension !== NONE ? [dimension] : [];
		this.args.onChange(dimensions);
	}

	@action
	onChangeMultiple(dimensions: string[] | null) {
		this.args.onChange(dimensions || []);
	}

	@action
	onHorizonOfferRequestKeyChange() {
		void this.loadHorizonOffer();
	}

	private async loadHorizonOffer() {
		const objectId = this.horizonMetricObjectId;
		const key = this.horizonOfferRequestKey;
		this.requestedHorizonOfferKey = key;
		if (!objectId) {
			return;
		}
		const dimensions = [...this.args.metricSetup.dimensions];
		// nothing picked narrows nothing, so there is nothing to ask
		if (!dimensions.length) {
			this.loadedHorizonOffer = { key, offer: null };
			return;
		}
		let offer: HorizonBreakdownOffer | null = null;
		try {
			offer = await this.mmHorizonMetricsService.resolveBreakdowns(objectId, dimensions);
		} catch (_e) {
			// keep the full list; the save refuses an impossible combination
		}
		if (this.requestedHorizonOfferKey === key && !this.isDestroying && !this.isDestroyed) {
			this.loadedHorizonOffer = { key, offer };
		}
	}
	<template>
		{{#if @isMultiple}}
			<FormSelectComponent
				name={{this.uniqueId}}
				@selected={{this.selectedMultiple}}
				@options={{this.options}}
				@onChange={{this.onChangeMultiple}}
				@isNumeric={{false}}
				@isMultiple={{true}}
				@maxSelectionLength={{MAX_DIMENSIONS}}
				@refreshOpenDropdown={{true}}
				@placeholder={{this.placeholder}}
				@floatlabelText={{this.floatlabelText}}
				@isDisabled={{@isDisabled}}
				@isRequired={{true}}
				@tooltipOnListFromProperty="description"
				@tooltipOnSelectionFromProperty=""
				@extraClass="dropdown-dimensions"
				@showFloatlabel={{@showFloatlabel}}
				@theme={{@theme}}
				@size={{@size}}
			/>
		{{else}}
			<FormSelectComponent
				name={{this.uniqueId}}
				@selected={{this.selectedSingle}}
				@options={{this.options}}
				@onChange={{this.onChangeSingle}}
				@isNumeric={{false}}
				@placeholder={{this.placeholder}}
				@floatlabelText={{this.floatlabelText}}
				@isDisabled={{@isDisabled}}
				@isRequired={{true}}
				@tooltipOnSelectionFromProperty=""
				@showFloatlabel={{@showFloatlabel}}
				@theme={{@theme}}
				@size={{@size}}
			/>
		{{/if}}

		<RowErrorComponent @items={{this.errors}} />
		<span {{didUpdate this.onHorizonOfferRequestKeyChange this.horizonOfferRequestKey}}></span>

		{{#if this.invalidDimensionErrorText}}
			<span class="error-message">{{this.invalidDimensionErrorText}}</span>
		{{else if this.refusedDimensionErrorText}}
			<span class="error-message">{{this.refusedDimensionErrorText}}</span>
		{{/if}}
	</template>
}
